import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import CollapsibleCardV2 from "../basics/interface/CollapsibleCardV2"
import { URL } from "../../store/actions/depannageActions"
import { useGroups } from "../../hooks/groups"
import { COLORS } from "../../utilities/Constants"

/**
 * Notes système (Jordan 19.10) : historique des actions sensibles d'une
 * fiche ou d'une mission (argent, documents, forçages, automatique), écrit
 * par le serveur (utilitaires/notes_systeme), lu par le BOSS seulement
 * (api/notes-systeme) — rien n'est rendu pour les autres.
 *
 * - `HistoriqueCardV2` : carte repliée, dernière carte des 3 fiches.
 * - `HistoriqueRepliable` : lien « Afficher l'historique (N) » (section de
 *   la mission, DepMissionV2, sous « Dispatch »).
 * Props : `domaine` (depannage | atelier | location | mission), `objetId`,
 * `rafraichir` (toute valeur qui change après un enregistrement → relu).
 */

const CATEGORIES = {
	argent: {
		label: "Argent",
		couleur: COLORS.accent,
		fond: COLORS.accentLight,
	},
	documents: {
		label: "Documents",
		couleur: COLORS.gold,
		fond: COLORS.warningLight,
	},
	forcage: {
		label: "Forçage",
		couleur: COLORS.danger,
		fond: COLORS.dangerLight,
	},
	automatique: {
		label: "Automatique",
		couleur: COLORS.textMuted,
		fond: COLORS.bg,
	},
}

const quand = (iso) => {
	const d = new Date(iso)
	const deux = (n) => String(n).padStart(2, "0")
	return `${deux(d.getDate())}.${deux(d.getMonth() + 1)}.${d.getFullYear()} ${deux(d.getHours())}:${deux(d.getMinutes())}`
}

const useNotesSysteme = (domaine, objetId, rafraichir) => {
	const { token } = useSelector((state) => state.navInfo.login)
	const { isBoss } = useGroups()
	const actif = isBoss && Number.isInteger(objetId) && objetId > 0
	const [etat, setEtat] = useState({ notes: [], erreur: null, charge: false })
	useEffect(() => {
		if (!actif) return
		let vivant = true
		fetch(
			`${URL}api/notes-systeme?domaine=${domaine}&objet_id=${objetId}`,
			{
				headers: { Authorization: `Bearer ${token}` },
			},
		)
			.then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
			.then((notes) => {
				if (vivant) setEtat({ notes, erreur: null, charge: true })
			})
			.catch(() => {
				if (vivant)
					setEtat({
						notes: [],
						erreur: "Historique indisponible.",
						charge: true,
					})
			})
		return () => {
			vivant = false
		}
	}, [actif, domaine, objetId, rafraichir, token])
	return { actif, ...etat }
}

const Liste = ({ notes, erreur, charge }) => {
	if (!charge) return <div style={styles.vide}>Chargement…</div>
	if (erreur) return <div style={styles.vide}>{erreur}</div>
	if (!notes.length)
		return (
			<div style={styles.vide}>Aucune action sensible enregistrée.</div>
		)
	return (
		<div style={{ display: "flex", flexDirection: "column" }}>
			{notes.map((n) => {
				const c = CATEGORIES[n.categorie] || CATEGORIES.automatique
				return (
					<div key={n.id} style={styles.ligne}>
						<span style={styles.quand}>{quand(n.le)}</span>
						<span
							style={{
								...styles.pastille,
								color: c.couleur,
								backgroundColor: c.fond,
							}}
						>
							{c.label}
						</span>
						<span style={styles.texte}>{n.texte}</span>
						<span style={styles.auteur}>
							{n.auteur || "automatique"}
						</span>
					</div>
				)
			})}
		</div>
	)
}

const HistoriqueCardV2 = ({ domaine, objetId, rafraichir = null }) => {
	const h = useNotesSysteme(domaine, objetId, rafraichir)
	if (!h.actif) return null
	return (
		<CollapsibleCardV2
			title="Historique"
			defaultOpen={false}
			right={
				h.charge && !h.erreur ? (
					<span style={styles.compteur}>{h.notes.length}</span>
				) : null
			}
		>
			<Liste {...h} />
		</CollapsibleCardV2>
	)
}

// `entete` : titre de section rendu seulement pour le boss.
export const HistoriqueRepliable = ({
	domaine,
	objetId,
	rafraichir = null,
	entete = null,
}) => {
	const h = useNotesSysteme(domaine, objetId, rafraichir)
	const [ouvert, setOuvert] = useState(false)
	if (!h.actif) return null
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{entete}
			<button
				type="button"
				style={styles.lien}
				onClick={() => setOuvert((o) => !o)}
			>
				{ouvert
					? "Masquer l'historique"
					: `Afficher l'historique${h.charge && !h.erreur ? ` (${h.notes.length})` : ""}`}
			</button>
			{ouvert && <Liste {...h} />}
		</div>
	)
}

const styles = {
	vide: { fontSize: 13, color: COLORS.textMuted },
	ligne: {
		display: "grid",
		gridTemplateColumns: "120px 96px 1fr auto",
		alignItems: "baseline",
		gap: 12,
		padding: "8px 0",
		borderBottom: `1px solid ${COLORS.border}`,
		fontSize: 13,
	},
	quand: { color: COLORS.textMuted, fontVariantNumeric: "tabular-nums" },
	pastille: {
		justifySelf: "start",
		padding: "2px 8px",
		borderRadius: 999,
		fontSize: 11,
		fontWeight: 600,
	},
	texte: { color: COLORS.text, overflowWrap: "anywhere" },
	auteur: { color: COLORS.textMuted, whiteSpace: "nowrap" },
	compteur: {
		minWidth: 22,
		padding: "2px 8px",
		borderRadius: 999,
		backgroundColor: COLORS.bg,
		color: COLORS.textMuted,
		fontSize: 12,
		fontWeight: 600,
		textAlign: "center",
	},
	lien: {
		alignSelf: "flex-start",
		background: "none",
		border: "none",
		padding: 0,
		color: COLORS.accent,
		fontSize: 13,
		cursor: "pointer",
	},
}

export default HistoriqueCardV2
